import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, subMonths, format, addDays } from "date-fns";

export interface ExtendedGymKPIs {
  // Original KPIs
  totalUsers: number;
  monthlyRevenue: number;
  previousMonthRevenue: number;
  revenueChange: number;
  attendanceRate: number;
  completedRoutines: number;
  routinesChange: number;
  
  // Extended KPIs
  retentionRate: number;
  averageTicket: number;
  newUsersMonth: number;
  penalizedUsers: number;
  classOccupancy: number;
  revenueGrowth: number;
}

export const useGymExtendedKPIs = (gymId: string) => {
  return useQuery({
    queryKey: ["gym-extended-kpis", gymId],
    queryFn: async (): Promise<ExtendedGymKPIs> => {
      const now = new Date();
      const startOfCurrentMonth = startOfMonth(now);
      const startOfPreviousMonth = startOfMonth(subMonths(now, 1));

      // Get users from this gym
      const { data: users } = await supabase
        .from("profiles")
        .select("id, created_at")
        .eq("gym_id", gymId);

      const userIds = users?.map(u => u.id) || [];
      const totalUsers = userIds.length;

      // New users this month
      const newUsersMonth = users?.filter(u => 
        new Date(u.created_at) >= startOfCurrentMonth
      ).length || 0;

      // Get current month revenue
      const { data: currentPayments } = await supabase
        .from("payments")
        .select("amount")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .gte("paid_at", startOfCurrentMonth.toISOString());

      const monthlyRevenue = currentPayments?.reduce((acc, p) => acc + Number(p.amount), 0) || 0;

      // Get previous month revenue
      const { data: previousPayments } = await supabase
        .from("payments")
        .select("amount")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .gte("paid_at", startOfPreviousMonth.toISOString())
        .lt("paid_at", startOfCurrentMonth.toISOString());

      const previousMonthRevenue = previousPayments?.reduce((acc, p) => acc + Number(p.amount), 0) || 0;
      const revenueChange = previousMonthRevenue > 0 
        ? ((monthlyRevenue - previousMonthRevenue) / previousMonthRevenue) * 100 
        : monthlyRevenue > 0 ? 100 : 0;

      // All payments for average ticket
      const { data: allPayments } = await supabase
        .from("payments")
        .select("amount")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users']);

      const averageTicket = allPayments && allPayments.length > 0
        ? allPayments.reduce((acc, p) => acc + Number(p.amount), 0) / allPayments.length
        : 0;

      // Get class attendance rate and penalized users
      const { data: attendance } = await supabase
        .from("class_attendance")
        .select("attended, penalized")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .gte("date", format(subMonths(now, 1), "yyyy-MM-dd"));

      const totalAttendance = attendance?.length || 0;
      const attendedCount = attendance?.filter(a => a.attended).length || 0;
      const attendanceRate = totalAttendance > 0 ? (attendedCount / totalAttendance) * 100 : 0;
      const penalizedUsers = attendance?.filter(a => a.penalized).length || 0;

      // Get completed routines this month
      const { data: routines } = await supabase
        .from("routine_progress")
        .select("id")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .gte("completed_at", startOfCurrentMonth.toISOString());

      const completedRoutines = routines?.length || 0;

      // Get previous month routines for comparison
      const { data: prevRoutines } = await supabase
        .from("routine_progress")
        .select("id")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .gte("completed_at", startOfPreviousMonth.toISOString())
        .lt("completed_at", startOfCurrentMonth.toISOString());

      const prevRoutinesCount = prevRoutines?.length || 0;
      const routinesChange = prevRoutinesCount > 0 
        ? ((completedRoutines - prevRoutinesCount) / prevRoutinesCount) * 100 
        : completedRoutines > 0 ? 100 : 0;

      // Calculate retention rate (users with active payments vs total)
      const { data: recentPayments } = await supabase
        .from("payments")
        .select("user_id, paid_at, validity_days")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .order("paid_at", { ascending: false });

      const activeUserIds = new Set<string>();
      recentPayments?.forEach(payment => {
        if (payment.paid_at && payment.validity_days) {
          const expiresAt = addDays(new Date(payment.paid_at), payment.validity_days);
          if (expiresAt >= now) {
            activeUserIds.add(payment.user_id);
          }
        }
      });
      
      const retentionRate = totalUsers > 0 ? (activeUserIds.size / totalUsers) * 100 : 0;

      // Get class occupancy
      const { data: classes } = await supabase
        .from("classes")
        .select(`
          id,
          capacity,
          class_subscriptions(count)
        `)
        .eq("gym_id", gymId);

      let totalCapacity = 0;
      let totalSubscriptions = 0;
      classes?.forEach(cls => {
        totalCapacity += cls.capacity;
        totalSubscriptions += cls.class_subscriptions?.[0]?.count || 0;
      });
      
      const classOccupancy = totalCapacity > 0 ? (totalSubscriptions / totalCapacity) * 100 : 0;

      // Revenue growth (3 month trend)
      const threeMonthsAgo = startOfMonth(subMonths(now, 3));
      const { data: threeMonthPayments } = await supabase
        .from("payments")
        .select("amount, paid_at")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .gte("paid_at", threeMonthsAgo.toISOString());

      const oldRevenue = threeMonthPayments
        ?.filter(p => new Date(p.paid_at!) < startOfPreviousMonth)
        .reduce((acc, p) => acc + Number(p.amount), 0) || 0;
      
      const revenueGrowth = oldRevenue > 0 
        ? ((monthlyRevenue - oldRevenue) / oldRevenue) * 100 
        : 0;

      return {
        totalUsers,
        monthlyRevenue,
        previousMonthRevenue,
        revenueChange,
        attendanceRate,
        completedRoutines,
        routinesChange,
        retentionRate,
        averageTicket,
        newUsersMonth,
        penalizedUsers,
        classOccupancy,
        revenueGrowth,
      };
    },
    enabled: !!gymId,
  });
};
